/**
 * PRESET-DE-RESOLVER — lógica pura de composición de la configuración de un preset de Lotowheel.
 *
 * Un preset del resolver `volatilidad-controlada` se guarda desde dos modos del Admin:
 *
 *   - CONFIG DIRECTA: el operador edita cada parámetro técnico y se guarda lo que escribió.
 *   - SLIDERS: el operador mueve cinco mandos de perfil, el backend los traduce a configuración
 *     técnica (`POST /admin/resolver-profiles/translate` → `buildConfigFromProfile`) y se guarda
 *     ESA traducción.
 *
 * El modo sliders necesita este módulo porque la traducción devuelve `rtp_target` y los parámetros
 * técnicos, pero NO los RTP por canal: esos el operador los edita aparte, en su propio bloque
 * visible. Guardar solo la traducción los perdía —el preset volvía a cargar con el default— y
 * guardar todo el formulario encima habría restaurado técnica vieja del DOM, anulando el cálculo de
 * los sliders que el operador acababa de pedir.
 *
 * Dos decisiones que este módulo fija, ambas por ser configuración MONETARIA:
 *
 *   1. La composición es EXPLÍCITA: la técnica sale de los sliders; de los targets por canal, solo
 *      los del formulario. Nada más cruza.
 *   2. Un valor inválido NO se omite: lanza. Omitirlo hacía que el resolver aplicara su default
 *      (0.85 para VLT) mientras el operador creía haber escrito otra cosa. Una entrada explícita
 *      ilegible debe detener la escritura, no desaparecer en silencio (CLAUDE.md §2.A fail-fast).
 *
 * Distinto de una config HISTÓRICA que no trae la clave: esa toma el default en runtime y así debe
 * seguir. La exigencia es sobre la ESCRITURA nueva.
 *
 * Módulo PURO — sin DOM, sin red, sin estado. El lector del formulario recibe el `document` por
 * parámetro, así que el front y su prueba ejecutan exactamente el mismo código.
 *
 * `shared/` es ESM. El backend y las pruebas (CJS, Node 22) lo importan con `require`.
 */

import {
    CANALES_DEL_EJE_RTP,
    RTP_TARGET_MINIMO,
    RTP_TARGET_MAXIMO,
    esRtpTargetDeCanalValido,
} from './canal-de-venta.js';

/**
 * Claves de RTP por canal que un preset de Lotowheel puede llevar, derivadas del catálogo del eje.
 *
 * Derivarlas evita mantener DOS allowlists que puedan divergir: el defecto original fue justo ese —el
 * formulario mostraba el campo y el guardado lo perdía—. Lo que la derivación NO hace es crear
 * configuración: para que un canal nuevo se RENDERICE y se pueda editar, el resolver debe declarar su
 * campo en el `configSchema`. El catálogo dice qué claves pertenecen al eje, no qué campos existen.
 */
export const CLAVES_DE_RTP_POR_CANAL = Object.freeze(CANALES_DEL_EJE_RTP.map(canal => canal.targetKey));

/** Prefijo de los `id` con que el Admin monta cada input de configuración del resolver. */
const PREFIJO_DE_INPUT_DE_CONFIG = 'resolverCfg_';

/**
 * Una entrada de RTP por canal que el operador escribió y no se puede usar. Lleva la `clave` para
 * que quien la capture pueda señalar el campo exacto sin parsear el mensaje.
 */
export class ErrorDeRtpPorCanalInvalido extends Error {
    constructor(clave, motivo) {
        super(
            `RTP por canal invalido en "${clave}": ${motivo}. ` +
                `Debe ser un numero entre ${RTP_TARGET_MINIMO} y ${RTP_TARGET_MAXIMO}.`
        );
        this.name = 'ErrorDeRtpPorCanalInvalido';
        this.clave = clave;
    }
}

/**
 * Convierte a número el texto de un input SIN tolerancia parcial.
 *
 * `parseFloat('0.8abc')` devuelve 0.8 y habría guardado un target que el operador nunca escribió;
 * `Number('')` devuelve 0, que además cae fuera de rango por casualidad y no por criterio. Se exige
 * que la cadena ENTERA sea un número finito.
 *
 * @param {string|number} valorCrudo
 * @returns {number} NaN si el texto no es íntegramente un número
 */
function aNumeroEstricto(valorCrudo) {
    if (typeof valorCrudo === 'number') return valorCrudo;
    if (typeof valorCrudo !== 'string') return NaN;
    const texto = valorCrudo.trim();
    if (texto === '') return NaN;
    return Number(texto);
}

/**
 * Lee del formulario los RTP por canal EXIGIENDO los tres, y lanza en la primera entrada inválida.
 *
 * Los ids se derivan de la misma lista que usa el renderizado, así que mostrar y guardar no pueden
 * divergir. Un input ausente del DOM también es un error: significa que el formulario no ofreció el
 * campo que este preset necesita, y guardar sin él dejaría que el resolver eligiera por el operador.
 *
 * @param {Document|{getElementById: function(string): ({value: string}|null)}} documento
 * @returns {Object} `{ rtpTargetWallet, rtpTargetTicket, rtpTargetVlt }`, todos números válidos
 * @throws {ErrorDeRtpPorCanalInvalido} en la primera clave ausente, ilegible o fuera de rango
 */
export function leerRtpPorCanalExigiendoTodos(documento) {
    const valores = {};
    for (const clave of CLAVES_DE_RTP_POR_CANAL) {
        const input = documento.getElementById(PREFIJO_DE_INPUT_DE_CONFIG + clave);
        if (!input) throw new ErrorDeRtpPorCanalInvalido(clave, 'el campo no esta en el formulario');
        valores[clave] = aNumeroEstricto(input.value);
    }
    return exigirLosTresRtpPorCanal(valores);
}

/**
 * Valida que estén los tres targets y que cada uno sea utilizable. Devuelve un objeto nuevo con solo
 * esas claves: lo que entre de más no llega al preset.
 *
 * @param {Object} rtpPorCanal
 * @returns {Object} los tres targets, validados
 * @throws {ErrorDeRtpPorCanalInvalido}
 */
function exigirUnRtpTargetUtilizable(clave, valor) {
    if (valor === null) throw new ErrorDeRtpPorCanalInvalido(clave, 'es null');
    if (typeof valor !== 'number' || Number.isNaN(valor)) {
        throw new ErrorDeRtpPorCanalInvalido(clave, 'no es un numero');
    }
    if (!Number.isFinite(valor)) throw new ErrorDeRtpPorCanalInvalido(clave, 'no es finito');
    if (!esRtpTargetDeCanalValido(valor)) throw new ErrorDeRtpPorCanalInvalido(clave, `${valor} esta fuera de rango`);
    return valor;
}

function exigirLosTresRtpPorCanal(rtpPorCanal) {
    const validados = {};
    for (const clave of CLAVES_DE_RTP_POR_CANAL) {
        const valor = rtpPorCanal?.[clave];
        if (valor === undefined) throw new ErrorDeRtpPorCanalInvalido(clave, 'falta un valor');
        validados[clave] = exigirUnRtpTargetUtilizable(clave, valor);
    }
    return validados;
}

/**
 * Valida los RTP por canal de una config del resolver **ya persistible**, con la regla del BACKEND:
 * lo que no está, no se exige.
 *
 * Es la otra mitad del contrato, y su semántica es DISTINTA a propósito de la del formulario:
 *
 *   - clave AUSENTE  → permitida. Una config histórica que nunca declaró el canal toma el default
 *     del resolver al resolver el sorteo, exactamente como hasta hoy. Exigirla acá rompería todas
 *     las configuraciones vigentes y obligaría a una migración que el plan prohíbe.
 *   - clave PRESENTE → tiene que servir. Un `null`, un string, un NaN o un 1.4 escritos a propósito
 *     no son «sin opinión»: son una entrada explícita ilegible, y el sorteo terminaría abortando en
 *     el fail-fast previo al giro con dinero ya apostado. Se rechaza al escribir.
 *
 * El formulario exige los tres porque ahí el operador ESTÁ editando los tres campos; una API admin
 * recibe parches parciales de configuraciones de cualquier época. Misma regla por valor, distinta
 * regla de presencia.
 *
 * @param {Object} configDelResolver - `config.rawConfig.resolver.config` tal como se va a persistir
 * @throws {ErrorDeRtpPorCanalInvalido} nombrando la clave, para que el 400 diga qué campo arreglar
 */
export function exigirRtpPorCanalValidoSiEstaPresente(configDelResolver) {
    if (!configDelResolver || typeof configDelResolver !== 'object') return;
    for (const clave of CLAVES_DE_RTP_POR_CANAL) {
        if (!Object.prototype.hasOwnProperty.call(configDelResolver, clave)) continue;
        const valor = configDelResolver[clave];
        // `undefined` explícito equivale a ausente: es lo que produce un JSON.parse de una clave que
        // no vino, y tratarlo distinto de la ausencia sería una diferencia sin significado.
        if (valor === undefined) continue;
        exigirUnRtpTargetUtilizable(clave, valor);
    }
}

/**
 * Compone la config de un preset: la técnica de los sliders más los tres RTP por canal.
 *
 * @param {Object} configDeSliders - traducción del perfil de operador (autoridad de lo técnico)
 * @param {Object} rtpPorCanal - `{ rtpTargetWallet, rtpTargetTicket, rtpTargetVlt }`, los tres obligatorios
 * @returns {Object} config del preset lista para persistir
 * @throws {ErrorDeRtpPorCanalInvalido} si falta alguno o alguno no es utilizable — el caller NO debe
 *   emitir el POST: el preset quedaría con un target que el operador no escribió.
 */
export function componerConfigDePresetConRtpPorCanal(configDeSliders, rtpPorCanal) {
    return { ...configDeSliders, ...exigirLosTresRtpPorCanal(rtpPorCanal) };
}
